/**
 * The LINK door onto a Moon Exchange listing.
 *
 * `/play?listing=ID` is what a seller sends, and the person who opens it is one
 * of three readers: the seller, another player, or someone with no account at
 * all. This module's whole job is to work out which -- and to hand the shared
 * listing view the same `viewer` fact-set the exchange grid hands it when a row
 * is clicked, so the two doors cannot draw different screens.
 *
 * The order matters and is deliberate:
 *
 *   1. The CARD opens immediately. It is identical for every reader and needs
 *      no session, so nothing about identity may delay it. An earlier version
 *      waited for auth and a visitor -- who never resolves -- got the guest
 *      tutorial instead of the listing they followed.
 *   2. The ACTION waits. Drawing "create an account" at a signed-in player and
 *      swapping it a moment later is a visible lie; a placeholder of the same
 *      size is not, and nothing moves when the real button lands.
 *   3. Signing up KEEPS THE LISTING. The reader was sent to the sign-in by this
 *      screen, so this screen comes back when they return from it.
 */

import {
  listingIdFromLocation,
  fetchPublicListing,
  fetchListingCard,
  appLooksSignedIn,
  displayedStardust,
  persistedSessionState,
  watchSignIn,
  fetchViewerFacts,
} from './exchange-deep-link.js';
import {
  openListingDetail,
  updateListingDetail,
  closeListingDetail,
} from './exchange-listing-detail.js';
import { MoonfallCloud } from '../convex-client.js';
import { i18n } from '../i18n.js';
import { paintStardustChip } from './stardust-chip.js';

/*
 * HOW LONG TO KEEP ASKING "is this listing mine".
 *
 * The Convex client exists before its auth does, so the first ask can come back
 * unanswered on a cold start. A FLAT 700ms retry made the common case — auth
 * ready a few tens of milliseconds later — cost the full 700, for nothing. The
 * backoff starts at 50ms and doubles, so a reader whose session is already warm
 * gets their button on the second attempt and a genuinely slow start still gets
 * ~6.3s of patience out of nine tries.
 */
const VIEWER_FACT_DELAYS_MS = [50, 100, 200, 400, 700, 1000, 1300, 1300, 1300];

/* The longest the card's own words may hold the first frame (fetchListingCard):
   past it the panel opens on the listing's words, which then stay. */
const LISTING_CARD_CAP_MS = 2500;

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** Repaint the top bar's balance, the same way the lobby does — through the
 *  ONE chip painter, so its format (the bare number; the Stardust glyph is
 *  CSS) can never diverge from the lobby's. This used to render `withSymbol`
 *  itself and would have printed a second mark beside the glyph. */
export function paintStardust(value) {
  if (!Number.isFinite(value)) return;
  paintStardustChip(document.getElementById('user-stardust'), value);
}

async function resolveViewerFacts(listingId, cloudNow, shouldStop) {
  /* `cloudNow` is re-read every attempt: the arrival can start before the app
     has published `window.MoonfallCloud` at all, and capturing it once would
     retry against a client that was undefined at the first ask. */
  for (const delay of VIEWER_FACT_DELAYS_MS) {
    if (shouldStop?.()) return null;
    const facts = await fetchViewerFacts(listingId, cloudNow());
    if (facts) return facts;
    await wait(delay);
  }
  return shouldStop?.() ? null : fetchViewerFacts(listingId, cloudNow());
}

/**
 * @param {object} [deps] injected for tests; production passes nothing
 */
export function startExchangeListingArrival(deps = {}) {
  const {
    search,
    /* THE IMPORTED MODULE, not `window.MoonfallCloud`. That global exists only
       in the bridged bundle, so the ownership check resolved under Playwright
       and returned `undefined` on a real page — green tests, dead feature. */
    cloud = () => MoonfallCloud,
    showSignIn = () => {},
    openExchange = () => {},
    /* The app's OWN Firebase subscription. It is passed in rather than imported
       so this module stays testable and so the arrival never owns an auth
       dependency it would otherwise have to keep in step. */
    subscribeAuth,
  } = deps;

  const listingId = listingIdFromLocation(search);
  if (!listingId) return null;

  /* Set when the reader chooses to leave — the X, Escape, the backdrop, or
     Browse. Being closed so the sign-in they asked for is reachable is NOT
     leaving, and is the one case that brings the panel back. */
  let dismissed = false;
  /* Set once the reader is DEFINITIVELY a visitor — Firebase answered `null`,
     or the backstop expired. A player answer that lands afterwards is stale and
     must not reinstate Buy for somebody with no account. */
  let wentGuest = false;
  /* One resolution at a time. The persisted-session read and the sign-in
     watcher can both want to start it. */
  let resolving = false;
  let listing = null;
  // The card in the reader's language, read before the first frame: it names the listing.
  let card = null;
  let stopWatching = () => {};

  const cloudNow = () => (typeof cloud === 'function' ? cloud() : cloud);

  async function buy(current, quantity) {
    const api = cloudNow()?.moonExchange;
    if (!api?.buyFromListing) throw new Error('Not connected');
    const result = await api.buyFromListing(current.id, quantity);
    if (Number.isFinite(result?.buyerStardust)) paintStardust(result.buyerStardust);
    return result;
  }

  async function cancel(current) {
    const api = cloudNow()?.moonExchange;
    if (!api?.cancelListing) throw new Error('Not connected');
    return api.cancelListing(current.id);
  }

  function present(viewer) {
    openListingDetail(listing, {
      door: 'link',
      card,
      viewer,
      buy,
      cancel,
      onSignUp: () => {
        /* Closed with a reason, not dismissed: `showFirebaseSignIn` reveals
           `#auth-view`, and leaving this dialog stacked over it would hide the
           thing the reader just asked for. */
        closeListingDetail('signin');
        showSignIn();
      },
      onBrowse: () => {
        closeListingDetail('browse');
        openExchange();
      },
      onClose: (reason) => {
        /* `signin` is the one reason that is NOT a dismissal: the reader was
           sent to the sign-in by this screen, so this screen comes back. Both
           of the others are the reader choosing to leave — pressing Browse
           means they moved on, and a later ownership answer must not re-open
           the listing on top of the Exchange they just opened. */
        if (reason === 'signin' || reason === 'replaced') return;
        dismissed = true;
        /* Stop the watcher rather than leaving a timer running for the life of
           the page on a screen nobody is looking at. */
        stopWatching();
      },
    });
  }

  /*
   * THE OWNERSHIP QUERY, STARTED AS EARLY AS THE LISTING FETCH.
   *
   * These are two independent round trips — one to our own server for the card,
   * one to Convex for "is it yours" — and running them back to back cost their
   * SUM. Measured on a real signed-in browser against the dev backend: the panel
   * painted at 438ms and the button did not follow until 881ms. Overlapped,
   * the button waits only for the slower of the two.
   *
   * The query needs nothing but the listing id, so nothing forces it to wait.
   */
  let factsPromise = null;
  function beginViewerResolve() {
    if (factsPromise || dismissed || wentGuest) return factsPromise;
    factsPromise = resolveViewerFacts(listingId, cloudNow, () => dismissed || wentGuest)
      .finally(() => { factsPromise = null; });
    return factsPromise;
  }

  async function toPlayer() {
    if (dismissed || wentGuest || resolving) return;
    resolving = true;
    let facts = null;
    try {
      facts = await (beginViewerResolve() || Promise.resolve(null));
    } finally {
      resolving = false;
    }
    if (dismissed || wentGuest) return;
    /*
     * AN AUTHENTICATED ANSWER IS ITSELF THE PROOF OF A SESSION.
     *
     * This used to require `appLooksSignedIn()` — the username painted in the
     * top bar — which is downstream of the app's own Convex profile read and
     * measured ~200ms LATER than the cloud client is usable. Waiting for it put
     * the reader's own profile round trip in front of this one for no reason.
     * `listingForViewer` resolving `exists: true` can only happen for a
     * signed-in caller, so it answers the same question, sooner. When it cannot
     * be answered at all, the marker is still consulted below.
     */
    if (!facts && !appLooksSignedIn()) return;
    /* THE SERVER'S NUMBER, not the top bar's. The top bar paints later than
       this answer arrives, so scraping it produced a facts line with no balance
       on the link door while the row door showed one -- the two doors
       disagreeing about the one number a buyer decides on. The scrape survives
       only for the case the server could not answer at all. */
    const balance = Number.isFinite(facts?.stardustBalance)
      ? facts.stardustBalance
      : displayedStardust();

    const viewer = facts
      ? {
        state: 'player',
        isMine: facts.isMine,
        stardustBalance: balance,
        exchangeUnlock: facts.exchangeUnlock,
      }
      /* Could not be answered. Not "not yours" -- guessing that is the bug this
         path exists to prevent -- so no action is offered at all. */
      : { state: 'player', isMine: false, actionable: false, stardustBalance: balance };
    if (facts) {
      listing = {
        ...listing,
        unitPrice: facts.unitPrice,
        quantityRemaining: facts.quantityRemaining,
        available: facts.available,
      };
    }

    if (!updateListingDetail({ viewer, listing, door: 'link' })) present(viewer);
    /* Answered. Nothing else this watcher can learn. */
    if (facts) stopWatching();
  }

  const ready = (async () => {
    /* BOTH AT ONCE. The card comes from the server; whether this device has a
       session comes from IndexedDB. Neither waits on the other, and neither
       waits on the app. */
    const listingRequest = fetchPublicListing(listingId).catch(() => null);
    const session = await persistedSessionState();
    /* The ~5ms IndexedDB answer is in; if there is a session, the Convex query
       goes out NOW rather than after the card comes back. */
    if (session !== 'none') beginViewerResolve();
    listing = await listingRequest;
    /* An id that resolves to nothing at all is a bad link, and the app behind
       it is a better landing than a dialog explaining a typo. A listing that
       merely SOLD OUT does resolve, and says so. */
    if (!listing?.id) return null;

    /*
     * THE CARD'S OWN WORDS ARE ON THE FIRST FRAME (28 September 2026).
     *
     * The listing carries its snapshot — the words stored, in English, when
     * the card was listed — and the exchange row names the card from the card
     * itself, in the reader's language. So this door reads that card before
     * it opens, and never opens on the snapshot to swap it a moment later.
     * Capped: a read that never answers opens on the listing's words, which
     * then stay; a card the route cannot give keeps them too.
     */
    card = await Promise.race([
      fetchListingCard(listing.cardId, {
        // The app's i18n has read the link's `?lang=` and the saved choice by now.
        lang: i18n?.getLanguage?.() || 'en',
        token: cloudNow()?.getAuthToken?.() || null,
      }),
      wait(LISTING_CARD_CAP_MS).then(() => null),
    ]);

    /*
     * A VISITOR GETS THEIR BUTTON ON THE FIRST PAINT.
     *
     * `session === 'none'` means this device has no persisted Firebase session
     * at all, so `onAuthStateChanged` can only ever answer `null`. That is not
     * a guess and it is not a timer — it is the same fact, read ~5ms earlier
     * from the storage the answer would have come out of anyway. Anything else
     * (`session` or `unknown`) still opens as `resolving` and waits for the
     * real answer, because "is this listing yours" needs a round trip and
     * drawing Buy on the seller's own card is the defect this door was rebuilt
     * for.
     */
    present({ state: session === 'none' ? 'guest' : 'resolving' });
    if (session === 'none') wentGuest = true;
    /* START ASKING NOW. A persisted session means the answer is worth chasing
       before the app has finished booting, let alone painted a username. */
    else void toPlayer();

    /* No separate "already signed in" call: `watchSignIn` ticks once
       synchronously and announces a reader who is already there. A second call
       here would race its own `stopWatching` assignment for the right to stop
       the watcher it has not been handed yet. */
    stopWatching = watchSignIn(
      () => {
        /* They were a visitor and now they are not — most often because this
           very screen sent them to create an account. A stale-answer guard must
           not become a reason to refuse the answer they came back for. */
        wentGuest = false;
        void toPlayer();
      },
      {
        subscribeAuth,
        /* UPDATE ONLY, NEVER RE-OPEN. If the panel is gone at this moment it is
           because the reader pressed "create an account" and is standing in the
           sign-in; re-opening the listing on top of it would bury the thing
           they just asked for. Only a completed sign-in brings it back. */
        onSettled: () => {
          if (dismissed || appLooksSignedIn()) return;
          wentGuest = true;
          updateListingDetail({ viewer: { state: 'guest' }, door: 'link' });
        },
      },
    );
    return listing;
  })();

  return { ready, stop: () => stopWatching() };
}

export const MoonfallExchangeArrival = { startExchangeListingArrival, paintStardust };
